/* Theme custom file */
import { startViewTransition } from '@theme/utilities';

const STYLES_ID = 'tab-navigation-styles';

class TabNavigation extends HTMLElement {
  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    TabNavigation.injectStyles();
    const { signal } = this.#controller;

    this.tabList = this.querySelector('[role="tablist"]');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];

    this.tabList.addEventListener('click', this.#handleClick, { signal });
    this.tabList.addEventListener('keydown', this.#handleKeydown, { signal });
    this.activate(0, false);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;

    const index = this.tabs.indexOf(tab);
    if (index !== -1) {
      this.activate(index);
      this.#scrollTabIntoView(index);
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const currentIndex = this.tabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(nextIndex);
    this.#scrollTabIntoView(nextIndex);
    this.tabs[nextIndex].focus();
  };

  /**
   * Horizontally scrolls the tablist so the target tab sits at the center.
   * The browser clamps scroll at 0 and max, so the first and last tabs naturally
   * stay pinned to their respective edges, which signals end-of-list to the user.
   * @param {number} index
   */
  #scrollTabIntoView(index) {
    const tab = this.tabs[index];
    if (!tab || !this.tabList) return;

    const tabCenter = tab.offsetLeft + tab.offsetWidth / 2;
    const targetScroll = tabCenter - this.tabList.clientWidth / 2;
    this.tabList.scrollTo({ left: targetScroll, behavior: 'smooth' });
  }

  /**
   * @param {number} index
   * @param {boolean} [animate=true] Slide the active-tab indicator via a View Transition.
   */
  activate(index, animate = true) {
    // Moving just the ARIA state inside Horizon's native View Transition makes the
    // active-tab pill (named in CSS while [data-tab-transitioning] is set) FLIP-slide
    // from the old tab to the new one.
    const moveIndicator = () => {
      for (const [i, tab] of this.tabs.entries()) {
        const isActive = i === index;
        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      }
    };

    // Panels swap after the slide so they are not captured by the indicator
    // transition; the revealed panel's cards cascade in via their own reveal.
    const swapPanels = () => {
      for (const [i, panel] of this.panels.entries()) {
        panel.hidden = i !== index;
      }
    };

    if (animate) {
      this.setAttribute('data-tab-transitioning', '');
      startViewTransition(moveIndicator).finally(() => {
        this.removeAttribute('data-tab-transitioning');
        swapPanels();
      });
    } else {
      moveIndicator();
      swapPanels();
    }
  }

  static injectStyles() {
    if (document.getElementById(STYLES_ID)) return;

    const style = document.createElement('style');
    style.id = STYLES_ID;
    style.textContent = `
      tab-navigation {
        display: block;
      }

      /* Tab bar */
      tab-navigation [role="tablist"] {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 8px;
        overflow-x: auto;
        padding: 8px;
        border-radius: 12px;
        background: rgb(var(--color-foreground-rgb) / 0.03);
        width: fit-content;
        max-width: 100%;
        margin-inline: auto;
        scrollbar-width: thin;
      }

      @media (max-width: 749px) {
        tab-navigation [role="tablist"] {
          scrollbar-width: none;
        }
      }

      /* Tab buttons */
      tab-navigation [role="tab"] {
        appearance: none;
        position: relative;
        isolation: isolate;
        background: transparent;
        border: none;
        color: rgb(var(--color-foreground-rgb) / 0.68);
        font-family: var(--font-body--family);
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.09em;
        line-height: 1.2;
        text-transform: uppercase;
        white-space: nowrap;
        padding: 12px 16px;
        border-radius: 6px;
        cursor: pointer;
        transition: color 0.2s ease, background-color 0.2s ease;
      }

      tab-navigation [role="tab"]:hover {
        color: var(--color-foreground);
      }

      tab-navigation [role="tab"]:focus-visible {
        outline: 2px solid var(--color-foreground);
        outline-offset: -2px;
      }

      tab-navigation [role="tab"][aria-selected="true"] {
        color: var(--color-foreground);
      }

      /* Active-tab pill, rendered behind the label so a View Transition can slide
         it between tabs. Named only while a switch is in flight (the component sets
         [data-tab-transitioning]) so it never interferes with page transitions. */
      tab-navigation [role="tab"][aria-selected="true"]::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        border-radius: 6px;
        background: var(--color-background);
      }

      @media (prefers-reduced-motion: no-preference) {
        tab-navigation[data-tab-transitioning] [role="tab"][aria-selected="true"]::before {
          view-transition-name: tab-nav-indicator;
        }
      }

      /* Panels */
      tab-navigation [role="tabpanel"] {
        display: block;
        margin-block-start: 24px;
      }

      @media (min-width: 750px) {
        tab-navigation [role="tabpanel"] {
          margin-block-start: 48px;
        }
      }

      tab-navigation [role="tabpanel"][hidden] {
        display: none;
      }
    `;

    document.head.appendChild(style);
  }
}

customElements.define('tab-navigation', TabNavigation);
